/**
 * Alerts Page
 * Displays violation alerts for the customer with filtering.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

import AppBar from '../elements/AppBar';
import PageBody from '../elements/PageBody';
import PageTitle from '../elements/PageTitle';
import FilterSection from '../elements/FilterSection';
import useProjectSelector from '../../hooks/useProjectSelector';
import { useAppSelector } from '../../store/hooks';
import { formatDateTime } from '../../utils/helpers';
import * as api from '../../services/api';

const ALERT_TYPE_COLORS = {
  DO_NOT_ENTER: 'error',
  DIRTY_IN_ORGANIC: 'warning',
  MOVE_RULE_VIOLATION: 'warning',
  GATEWAY_OFFLINE: 'default',
};

const AlertsPage = () => {
  const { activeCustomer } = useAppSelector((state) => state.customer);
  const { ProjectSelectorComponent } = useProjectSelector();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const result = activeCustomer?.id
        ? await api.listCustomerAlerts(activeCustomer.id)
        : await api.listAlerts();
      setAlerts(result.items || []);
    } catch (err) {
      console.error('Error loading alerts:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCustomer?.id]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const filtered = typeFilter
    ? alerts.filter((a) => a.alertType === typeFilter)
    : alerts;

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent,
        }}
      />

      <PageBody>
        {/* Title Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <PageTitle>
            Violation Alerts ({filtered.length})
          </PageTitle>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={loadAlerts} 
            disabled={loading}
            sx={{ height: 44 }}
          >
            Refresh
          </Button>
        </Box>

        {/* Filter Row */}
        <FilterSection>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Alert Type</InputLabel>
              <Select 
                value={typeFilter} 
                label="Alert Type" 
                onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="DO_NOT_ENTER">Do Not Enter</MenuItem>
                <MenuItem value="DIRTY_IN_ORGANIC">Dirty in Organic</MenuItem>
                <MenuItem value="MOVE_RULE_VIOLATION">Move Rule Violation</MenuItem>
                <MenuItem value="GATEWAY_OFFLINE">Gateway Offline</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </FilterSection>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #4A4847' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Alert Type</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Email Sent To</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No alerts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paged.map((alert) => (
                    <TableRow key={alert.id} hover>
                      <TableCell>{formatDateTime(alert.createdAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={alert.alertType?.replace(/_/g, ' ')}
                          size="small"
                          color={ALERT_TYPE_COLORS[alert.alertType] || 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>{alert.message}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {alert.assetId?.substring(0, 12)}
                      </TableCell>
                      <TableCell>{alert.projectId || '-'}</TableCell>
                      <TableCell>{alert.emailSentTo || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </TableContainer>
        )}
      </PageBody>
    </Box>
  );
};

export default AlertsPage;
